/**
 * Suhbat holati — butun mantiq shu yerda, ko'rinishdan ajratilgan.
 *
 * Komponentlar faqat chizadi. Shuning uchun dizayn butunlay o'zgarsa ham
 * (yoki React Native ga ko'chirilsa) bu fayl o'zgarmaydi.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../api/client";

const FINAL = ["done", "failed"];

export const PHASES = {
  IDLE: "idle",
  THINKING: "thinking",
  ANSWERING: "answering",
};

export function useChat() {
  const [messages, setMessages] = useState([]);
  const [trace, setTrace] = useState([]);
  const [phase, setPhase] = useState(PHASES.IDLE);
  const [error, setError] = useState("");
  // `tools` javob xabariga emas, oxirgi so'rovga tegishli — alohida holat.
  const [meta, setMeta] = useState({ tools: [], goods: "" });
  const abortRef = useRef(null);
  // Tasvir vazifalari fonda (~1 daqiqa) — suhbat davom etaveradi
  const watchers = useRef(new Map());
  const photoUrls = useRef([]);
  // Suhbatlar tarixi: faol suhbat (chap panelda belgilanadi) va ro'yxatni yangilash hisoblagichi
  const [sessionId, setSessionId] = useState(api.getSessionId);
  const [chatKey, setChatKey] = useState(0);
  const opening = useRef(0);
  const shown = useRef([]); // `messages` ning oxirgi holati — useCallback ichidan o'qish uchun
  const pendingVisual = useRef(null);
  useEffect(() => {
    shown.current = messages;
  }, [messages]);
  const touched = () => setChatKey((k) => k + 1);

  const busy = phase !== PHASES.IDLE;

  useEffect(() => () => {
    abortRef.current?.abort();
    watchers.current.forEach((stop) => stop());
  }, []);

  const send = useCallback(
    async (text) => {
      const t = (text || "").trim();
      if (!t || busy) return;

      setError("");
      setTrace([]);
      setPhase(PHASES.THINKING);
      setSessionId(api.ensureSession());
      setMessages((m) => [
        ...m,
        { role: "user", text: t, id: api.uid(), at: Date.now() },
      ]);

      const controller = new AbortController();
      abortRef.current = controller;

      // Javob xabari birinchi bo'lak kelganda qo'shiladi — shunda
      // "o'ylayapti" bosqichi bilan javob bir vaqtda ko'rinmaydi.
      let opened = false;
      const botId = api.uid();

      const openBot = (patch = {}) => {
        if (opened) return;
        opened = true;
        setPhase(PHASES.ANSWERING);
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text: "",
            id: botId,
            done: false,
            at: Date.now(),
            ...patch,
          },
        ]);
      };

      const patchBot = (patch) =>
        setMessages((m) =>
          m.map((x) => (x.id === botId ? { ...x, ...patch } : x)),
        );

      try {
        await api.sendMessage(
          t,
          (event, data) => {
            switch (event) {
              case "session":
                api.setSessionId(data.session_id);
                setSessionId(data.session_id);
                break;
              case "trace":
                setTrace((tr) => [...tr, data.label]);
                break;
              case "tools":
                // Qidiruv ishlaganmi — CLI dagi `[tif_tn_qidir]` ning
                // aynan o'zi. LLM noto'g'ri qaror qilsa shu yerdan
                // ko'rinadi, shuning uchun yashirilmaydi.
                setMeta((x) => ({ ...x, tools: data.names, goods: data.goods }));
                break;
              case "meta": {
                const stats = {
                  code: data.code,
                  level: data.level,
                  ms: data.ms,
                  candidates: data.candidates ?? [],
                  levels: data.levels ?? [],
                };
                openBot(stats);
                patchBot(stats);
                break;
              }
              case "answer":
                openBot();
                setMessages((m) =>
                  m.map((x) =>
                    x.id === botId ? { ...x, text: x.text + data.text } : x,
                  ),
                );
                break;
              // Javob QAYSI rasmiy matnga tayangani. Iqtibossiz javobni
              // foydalanuvchi tekshira olmaydi — bojxona masalasida bu
              // muhim, chunki noto'g'ri javob jarima demakdir.
              case "sources":
                openBot();
                patchBot({ sources: data.items });
                break;
              case "warning":
                openBot();
                patchBot({ warning: data.text });
                break;
              case "error":
                setError(data.text);
                break;
              case "done":
                openBot();
                // `turnId` — baho shu javobga bog'lanadi. Bazaga
                // yozilmagan bo'lsa `null` keladi va baho paneli
                // ko'rsatilmaydi (yozib bo'lmaydigan bahoni so'rash
                // ekspert vaqtini behuda sarflaydi).
                patchBot({ done: true, turnId: data.turn_id ?? null });
                touched();
                break;
              default:
                break;
            }
          },
          controller.signal,
        );
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || String(e));
      } finally {
        setPhase(PHASES.IDLE);
        setTrace([]);
        abortRef.current = null;
      }
    },
    [busy],
  );

  const patch = (id, p) =>
    setMessages((m) => m.map((x) => (x.id === id ? { ...x, ...p } : x)));

  const settle = (msgId, v) => {
    watchers.current.delete(msgId);
    patch(msgId, { view: v, stage: v.stage });
  };

  const follow = (msgId, v) => {
    if (FINAL.includes(v.status)) return settle(msgId, v);
    patch(msgId, { view: v, stage: v.stage, product: v.product?.name });
    watchers.current.set(msgId, api.watchVisual(v.id, {
      onStage: ({ stage, product }) => patch(msgId, product ? { stage, product } : { stage }),
      onDone: (done) => settle(msgId, done),
    }));
  };

  // photos: [{file, thumb}] — `api.preparePhoto` natijasi
  const sendPhotos = useCallback(async (photos, hint = "") => {
    const urls = photos.map((p) => p.thumb).filter(Boolean);
    // Katta ko'rish uchun — serverga ketgan nusxa (2048 px), kichik 320 px emas
    const full = photos.map((p) => URL.createObjectURL(p.file));
    photoUrls.current.push(...urls, ...full);
    const msgId = api.uid();
    setError("");
    setSessionId(api.ensureSession());
    setMessages((m) => [
      ...m,
      { role: "user", text: hint.trim(), photos: urls, full, id: api.uid(), at: Date.now() },
      // files, hint — vazifa yaratilmay yiqilsa (tarmoq) qayta yuborish uchun
      { role: "visual", id: msgId, photo: urls[0], files: photos.map((p) => p.file), hint: hint.trim(),
        draw: false, stage: "uploading", view: null, at: Date.now() },
    ]);
    try {
      // Avval faqat tahlil va kodlar (~25 s); rasm va poster — so'ralganda (drawVisual)
      const v = await api.createVisual(photos.map((p) => p.file), { product_hint: hint.trim(), lang: "uz", draw: "0" });
      follow(msgId, v);
      // Tarix uchun; bog'lanmasa ham tasvirning o'zi ishlayveradi
      api.linkVisual(v.id, hint.trim()).then((sid) => {
        setSessionId(sid);
        touched();
      }, () => {});
    } catch (e) {
      patch(msgId, { view: { status: "failed", error: { code: e.code, message: e.message || String(e) } } });
    }
  }, []);

  const drawVisual = useCallback(async (m) => {
    const prev = m.view;
    patch(m.id, { view: null, stage: "queued", draw: true, product: prev.product?.name });
    try {
      follow(m.id, await api.drawVisual(prev.id));
    } catch (e) {
      patch(m.id, { view: { ...prev, drawError: e.message || String(e) } });
    }
  }, []);


  // Vazifa bor bo'lsa server saqlangan fotolardan qayta ishlaydi, yo'q bo'lsa fotolar qayta yuboriladi
  const retryVisual = useCallback(async (m) => {
    const jobId = m.view?.id;
    patch(m.id, { view: null, stage: jobId ? "queued" : "uploading" });
    try {
      const v = jobId
        ? await api.retryVisual(jobId)
        : await api.createVisual(m.files ?? [], { product_hint: m.hint ?? "", lang: "uz", draw: "0" });
      follow(m.id, v);
    } catch (e) {
      patch(m.id, { view: { ...m.view, status: "failed", error: { code: e.code, message: e.message || String(e) } } });
    }
  }, []);

  const clear = () => {
    abortRef.current?.abort();
    watchers.current.forEach((stop) => stop());
    watchers.current.clear();
    photoUrls.current.forEach(URL.revokeObjectURL);
    photoUrls.current = [];
    setMessages([]);
    setTrace([]);
    setError("");
    setMeta({ tools: [], goods: "" });
    setPhase(PHASES.IDLE);
  };

  const reset = useCallback(async () => {
    opening.current += 1;
    clear();
    setSessionId(null);
    try {
      await api.resetChat();
    } catch {
      /* backend yo'q bo'lsa ham interfeys tozalanadi */
    }
  }, []);

  // Tarixdan suhbat: savol-javoblar bazadan, tasvirlar o'z holati bilan (jarayondagisi kuzatiladi)
  // `focusJob` — tayyor bo'lganda shu tasvirga suriladi (boshqalari surmaydi)
  const openChat = useCallback(async (id, { quiet = false, focusJob = null } = {}) => {
    const token = ++opening.current;
    clear();
    api.setSessionId(id);
    setSessionId(id);
    let items;
    try {
      items = await api.getChat(id);
    } catch (e) {
      if (!quiet && token === opening.current) setError(e.message || String(e));
      return;
    }
    if (token !== opening.current) return; // boshqa suhbat ochildi
    const out = [];
    for (const it of items) {
      const at = Date.parse(it.at) || Date.now();
      if (it.type === "visual") {
        const shots = it.photos ?? [];
        if (it.hint || shots.length) {
          out.push({
            role: "user", text: it.hint, id: api.uid(), at,
            photos: shots.map((u) => api.apiUrl(u, { w: 320 })), full: shots.map((u) => api.apiUrl(u)),
          });
        }
        out.push({
          role: "visual", id: api.uid(), jobId: it.job_id, stage: "queued", view: null, at,
          ...(focusJob && { focus: it.job_id === focusJob }),
        });
        continue;
      }
      out.push({ role: "user", text: it.question, id: api.uid(), at });
      if (it.answer) {
        out.push({
          role: "bot", id: api.uid(), at, done: true, text: it.answer,
          code: it.code, level: it.level, ms: it.total_ms, candidates: it.candidates ?? [],
          levels: it.levels ?? [], sources: it.sources ?? [], warning: it.warning || undefined,
          turnId: it.id, rated: it.feedback?.length > 0,
        });
      }
    }
    setMessages(out);
    out.filter((m) => m.role === "visual").forEach((m) =>
      api.getVisual(m.jobId).then(
        (v) => token === opening.current && follow(m.id, v),
        (e) => token === opening.current &&
          patch(m.id, { view: { status: "failed", error: { message: e.message || String(e) } } }),
      ));
  }, []);

  // "Foto tahlillari" dan: ekranda bo'lsa — o'shanga suriladi; yaratilgan suhbati bo'lsa — o'sha suhbat;
  // aks holda bir marta qo'shiladi (ilgari har bosishda yangi nusxa qo'shilardi)
  const openVisual = useCallback(async (id) => {
    const here = () => shown.current.some((m) => m.role === "visual" && (m.view?.id ?? m.jobId) === id);
    const scroll = () =>
      document.querySelector(`[data-job="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (here()) return scroll();
    if (pendingVisual.current === id) return;
    pendingVisual.current = id;
    try {
      const sid = await api.visualChat(id);
      if (sid) return await openChat(sid, { focusJob: id });
      if (here()) return scroll();
      const msgId = api.uid();
      setMessages((m) => [...m, { role: "visual", id: msgId, jobId: id, stage: "queued", view: null, at: Date.now() }]);
      try {
        follow(msgId, await api.getVisual(id));
      } catch (e) {
        patch(msgId, { view: { status: "failed", error: { message: e.message || String(e) } } });
      }
    } finally {
      pendingVisual.current = null;
    }
  }, [openChat]);

  // Sahifa yangilanganda joriy suhbat qaytadi (server ham shu suhbat kontekstida davom etadi)
  useEffect(() => {
    const sid = api.getSessionId();
    if (sid) openChat(sid, { quiet: true });
  }, [openChat]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  return {
    messages, trace, phase, busy, error, meta, sessionId, chatKey,
    send, sendPhotos, openVisual, openChat, retryVisual, drawVisual, reset, stop,
  };
}
